import React from 'react';
import {classes, style} from './doppe-viewer-buy-or-claim-download-modal.st.css';
import {
    reactCreateMemo,
    useAsyncOperation,
    useEventCallback,
} from '@wix/devzai-utils-react';
import {AsyncValueStates, OmitStrict, valueConvertWhenNotNullable} from '@wix/devzai-utils-common';
import {DoppeViewerContentModal} from '../doppe-viewer-content-modal/doppe-viewer-content-modal';
import {DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {useDoppeViewerLang} from '../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import {
    DoppeViewerHorizontalButton,
    DoppeViewerHorizontalButtonPriority,
    DoppeViewerHorizontalButtonSkin
} from '../doppe-viewer-horizontal-button/doppe-viewer-horizontal-button';
import {
    DoppeViewerClaimDownloadForm
} from '../doppe-viewer-claim-download-form/doppe-viewer-claim-download-form';
import {DoppeActionPrice, doppeActionPriceGetFormattedPrice} from '../../client-server-common/types/doppe-action-price';
import {DoppeViewerScrollView} from '../doppe-viewer-scroll-view/doppe-viewer-scroll-view';

/**
 * Pre-checkout modal opened when a buyer clicks a *paid* Download File CTA.
 *
 * Mirrors the Payment Lock widget's layout in a modal form: a Buy section at the
 * top (product info + price + "Buy now") and an "Already purchased?" section at
 * the bottom that embeds the shared `DoppeViewerClaimDownloadForm` (same form
 * the post-checkout claim modal uses). The "Buy now" button delegates the
 * existing Wix Checkout-opening flow back to the caller via `onBuyClick`; the
 * modal closes itself before invoking it so the editor-preview iframe modal can
 * open cleanly without modal-inside-modal stacking.
 */
export const DoppeViewerBuyOrClaimDownloadModal = reactCreateMemo(
    function DoppeViewerBuyOrClaimDownloadModal(props: DoppeViewerBuyOrClaimDownloadModal.Props) {
        const {
            actionId,
            pageId,
            productName,
            description,
            priceData,
            onBuyClick,
            closeCallback,
            ...restProps
        } = props;

        return (
            <DoppeViewerContentModal
                mobileLayout={DoppeViewerModalPanelLayout.BottomDrawer}
                desktopLayout={DoppeViewerModalPanelLayout.Inner}
                showHeader={true}
                showHeaderAsCloseButton={true}
                title={productName}
                {...restProps}
                closeCallback={closeCallback}
                content={() => (
                    <DoppeViewerBuyOrClaimDownloadModalBody
                        actionId={actionId}
                        pageId={pageId}
                        productName={productName}
                        description={description}
                        priceData={priceData}
                        onBuyClick={onBuyClick}
                        closeCallback={closeCallback}
                    />
                )}
            />
        );
    }
);

const DoppeViewerBuyOrClaimDownloadModalBody = reactCreateMemo(
    function DoppeViewerBuyOrClaimDownloadModalBody(props: DoppeViewerBuyOrClaimDownloadModalBody.Props) {
        const {actionId, pageId, productName, description, priceData, onBuyClick, closeCallback} = props;

        const viewerLocales = useDoppeViewerLocales();
        const lang = useDoppeViewerLang();

        const [buyOperation, setBuyOperation] = useAsyncOperation<void>();
        const isBuyLoading = buyOperation?.state === AsyncValueStates.Pending;

        // The modal must close before the editor-preview iframe modal opens
        // (otherwise the Wix Checkout iframe would stack inside this modal).
        // For the live viewer the order is harmless: the page is about to
        // navigate to the Wix Checkout URL either way.
        const handleBuyClick = useEventCallback(() => {
            void setBuyOperation((async () => {
                closeCallback();
                await onBuyClick();
            })());
        });

        return (
            <DoppeViewerScrollView className={classes.root}>
                <div className={classes.inner}>
                    <div className={classes.header}>
                        <h2 className={classes.title}>{productName}</h2>
                        {valueConvertWhenNotNullable(description, d => (
                            <p className={classes.description}>{d}</p>
                        ))}
                        <div className={classes.price}>
                            {doppeActionPriceGetFormattedPrice(priceData, lang)}
                        </div>
                    </div>
                    <DoppeViewerHorizontalButton
                        className={style(classes.buyButton, {})}
                        priority={DoppeViewerHorizontalButtonPriority.Primary}
                        skin={DoppeViewerHorizontalButtonSkin.Default}
                        onClick={handleBuyClick}
                        disabled={isBuyLoading}
                        isLoading={isBuyLoading}
                    >
                        {viewerLocales['buyOrClaimDownload.dialog.buyButton']}
                    </DoppeViewerHorizontalButton>
                    <div className={classes.divider}>
                        {viewerLocales['buyOrClaimDownload.dialog.alreadyPaidDivider']}
                    </div>
                    <DoppeViewerClaimDownloadForm
                        actionId={actionId}
                        pageId={pageId}
                        onDownloadStarted={closeCallback}
                    />
                </div>
            </DoppeViewerScrollView>
        );
    }
);

export namespace DoppeViewerBuyOrClaimDownloadModal {
    export interface Props extends OmitStrict<DoppeViewerContentModal.Props, 'content' | 'title' | 'closeCallback'> {
        actionId: string;
        pageId: string;
        productName: string;
        priceData: DoppeActionPrice;
        /**
         * Optional product description rendered under the title. Typically the
         * `paymentSettings.customItemTypeDescription` visible value, when set.
         */
        description?: string;
        /**
         * Invoked when the buyer presses "Buy now". The caller is responsible
         * for opening Wix Checkout (live: redirect; editor preview: iframe
         * modal). The modal closes itself before invoking the callback.
         */
        onBuyClick: () => void | Promise<void>;
        closeCallback: () => void;
    }
}

namespace DoppeViewerBuyOrClaimDownloadModalBody {
    export interface Props {
        actionId: string;
        pageId: string;
        productName: string;
        description?: string;
        priceData: DoppeActionPrice;
        onBuyClick: () => void | Promise<void>;
        closeCallback: () => void;
    }
}
